import { useState, useEffect, useRef } from 'react';
import { parseExcelFile } from '../../utils/emendas/excelParser';
import { detectColumnTypes } from '../../utils/emendas/columnTypeDetector';
import { EXCEL_FILE_PATHS, EMENDA_TYPES } from '../../utils/emendas/constants';

// Cache global em memória para armazenar dados de ambas as abas
const dataCache = {
  [EMENDA_TYPES.MUNICIPAL]: null,
  [EMENDA_TYPES.ESTADUAL]: null
};

// Função para salvar cache no sessionStorage
const saveCacheToSession = (emendaType, data, columns) => {
  try {
    const cacheData = {
      data,
      columns,
      timestamp: Date.now()
    };
    sessionStorage.setItem(`emendas_cache_${emendaType}`, JSON.stringify(cacheData));
  } catch (err) {
    console.warn('Erro ao salvar cache no sessionStorage:', err);
  }
};

// Função para carregar cache do sessionStorage
const loadCacheFromSession = (emendaType) => {
  try {
    const cached = sessionStorage.getItem(`emendas_cache_${emendaType}`);
    if (cached) {
      const cacheData = JSON.parse(cached);
      const age = Date.now() - cacheData.timestamp;
      const maxAge = 30 * 60 * 1000; // 30 minutos
      
      if (age < maxAge) {
        return cacheData;
      } else {
        sessionStorage.removeItem(`emendas_cache_${emendaType}`);
      }
    }
  } catch (err) {
    console.warn('Erro ao carregar cache do sessionStorage:', err);
  }
  return null;
};

/**
 * Hook para carregar dados de emendas de um arquivo Excel com cache
 * @param {string} emendaType - Tipo de emenda (EMENDA_TYPES.MUNICIPAL ou EMENDA_TYPES.ESTADUAL)
 * @returns {Object} { data, columns, loading, error, emendaType }
 */
export const useEmendasData = (emendaType = EMENDA_TYPES.MUNICIPAL) => {
  const [data, setData] = useState([]);
  const [columns, setColumns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const isFirstLoad = useRef(true);

  useEffect(() => {
    const loadData = async () => {
      try {
        // 1. Verificar cache em memória (mais rápido)
        if (dataCache[emendaType]) {
          setData(dataCache[emendaType].data);
          setColumns(dataCache[emendaType].columns);
          setLoading(false);
          setError(null);
          return;
        }

        // 2. Verificar cache no sessionStorage (sobrevive a refresh)
        const sessionCache = loadCacheFromSession(emendaType);
        if (sessionCache) {
          // Restaurar também no cache em memória
          dataCache[emendaType] = sessionCache;
          setData(sessionCache.data);
          setColumns(sessionCache.columns);
          setLoading(false);
          setError(null);
          return;
        }

        setLoading(true);
        setError(null);

        const filePath = EXCEL_FILE_PATHS[emendaType];
        
        if (!filePath) {
          throw new Error(`Tipo de emenda inválido: ${emendaType}`);
        }

        const response = await fetch(filePath);
        
        if (!response.ok) {
          throw new Error(`Erro ao carregar arquivo: ${response.status} ${response.statusText}`);
        }

        const arrayBuffer = await response.arrayBuffer();
        
        const { data: parsedData, columns: parsedColumns } = parseExcelFile(arrayBuffer);
        const columnsWithTypes = detectColumnTypes(parsedData, parsedColumns);
        
        // Armazenar no cache em memória
        dataCache[emendaType] = {
          data: parsedData,
          columns: columnsWithTypes,
          timestamp: Date.now()
        };
        
        // Armazenar também no sessionStorage
        saveCacheToSession(emendaType, parsedData, columnsWithTypes);
        
        setData(parsedData);
        setColumns(columnsWithTypes);
      } catch (err) {
        console.error('Erro ao processar Excel:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [emendaType]);

  return { data, columns, loading, error, emendaType };
};

/**
 * Utilitário para gerenciar cache de emendas
 */
export const EmendasCacheManager = {
  /**
   * Limpa cache de um tipo específico de emenda
   */
  clearCache: (emendaType) => {
    if (emendaType) {
      dataCache[emendaType] = null;
      sessionStorage.removeItem(`emendas_cache_${emendaType}`);
    }
  },

  /**
   * Limpa todo o cache de emendas
   */
  clearAllCache: () => {
    Object.keys(EMENDA_TYPES).forEach(key => {
      const type = EMENDA_TYPES[key];
      dataCache[type] = null;
      sessionStorage.removeItem(`emendas_cache_${type}`);
    });
  },

  /**
   * Retorna informações sobre o cache
   */
  getCacheInfo: () => {
    const info = {};
    Object.keys(EMENDA_TYPES).forEach(key => {
      const type = EMENDA_TYPES[key];
      const memCache = dataCache[type];
      const sessionCache = loadCacheFromSession(type);
      
      info[type] = {
        inMemory: !!memCache,
        inSession: !!sessionCache,
        dataCount: memCache ? memCache.data.length : (sessionCache ? sessionCache.data.length : 0),
        age: memCache ? Math.round((Date.now() - memCache.timestamp) / 1000) : null
      };
    });
    return info;
  },

  /**
   * Pré-carrega dados de ambas as abas
   */
  preloadAll: async () => {
    const promises = Object.values(EMENDA_TYPES).map(async (type) => {
      if (!dataCache[type] && !loadCacheFromSession(type)) {
        try {
          const filePath = EXCEL_FILE_PATHS[type];
          const response = await fetch(filePath);
          const arrayBuffer = await response.arrayBuffer();
          const { data: parsedData, columns: parsedColumns } = parseExcelFile(arrayBuffer);
          const columnsWithTypes = detectColumnTypes(parsedData, parsedColumns);
          
          dataCache[type] = {
            data: parsedData,
            columns: columnsWithTypes,
            timestamp: Date.now()
          };
          saveCacheToSession(type, parsedData, columnsWithTypes);
        } catch (err) {
          console.error('Erro ao pré-carregar emendas:', err);
        }
      }
    });
    await Promise.all(promises);
  }
};
